<template>
  <generic-modal
    :value="value"
    :is-disabled="loading"
    :is-loading="loading"
    :is-confirm-disabled="isCreateDisabled"
    :title="modalTitle"
    persistent
    confirm-text="Create Article"
    hide-louis
    @modal-confirm="onCreateConfirm"
    @modal-cancel="onCreateCancel"
  >
    <template v-if="!inheritFields.site_group_id">
      <div class="d-flex text-caption mb-2">
        <v-icon class="mr-1" small>info</v-icon>
        <span>This modal is for <b>NEW</b> articles only. New articles will default to English.</span>
      </div>

      <div class="text-caption mb-10">
        To create a <b>TRANSLATED</b> news article, navigate to an existing article's EDIT view and select the
        'Languages' tab.
      </div>
    </template>

    <v-form ref="form">
      <autocomplete-input
        v-if="!inheritFields.site_group_id"
        v-model="formData.site_group_id"
        :items="siteGroups"
        :is-loading="loading"
        label="Site Group"
        hide-details
        class="mb-4"
      />

      <v-card v-if="(inheritFields.site_group_id || formData.site_group_id) && requiredCategorisedSchema" flat>
        <v-row v-for="(fields, category) in requiredCategorisedSchema.general" :key="category">
          <v-col>
            <category-options
              :category="category"
              :data="formData"
              :exclude="['site_id']"
              :fields="fields"
              :languages="categoryOptionLanguages"
              :show-category="false"
              :edit-mode="inheritFields.language_id != undefined"
              :users="authorOptions"
              :is-disabled="loading"
              :is-loading="loading"
              @input="onInput"
            />
          </v-col>
        </v-row>
      </v-card>

      <v-card
        v-if="(inheritFields.site_group_id || formData.site_group_id) && requiredCategorisedSchema"
        class="mt-4"
        outlined
      >
        <v-expansion-panels flat>
          <v-expansion-panel>
            <v-expansion-panel-header>
              Advanced
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <toggle-input v-model="formData.auto_translate" label="Automatically translate" />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>
    </v-form>
  </generic-modal>
</template>

<script>
// Vue
import { mapActions, mapGetters, mapState } from 'vuex'
// Services
import { wordpress, news, authors } from '@/services/offers'
// Helpers
import isSet from '@/utils/is-set'
// Components
import AutocompleteInput from '@/components/forms/autocomplete-input'
import CategoryOptions from '@/components/category-options'
import GenericModal from '@/components/modals/generic-modal'
import ToggleInput from '@/components/forms/toggle-input'

export default {
  components: { AutocompleteInput, CategoryOptions, GenericModal, ToggleInput },
  props: {
    inheritFields: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      authors: [],
      options: { authors: [] },
      formData: { auto_translate: false },
    }
  },
  computed: {
    ...mapState('newsArticles', ['schema', 'requiredCategorisedSchema']),
    ...mapGetters({
      siteGroups: 'siteGroups/getSiteGroupOptions',
      languageSites: 'languageSites/getLanguageSites',
    }),
    authorOptions() {
      const siteGroupId = this.inheritFields.site_group_id || this.formData.site_group_id
      return this.authors
        .filter(({ author }) => author.some(({ site_group_id }) => site_group_id === siteGroupId))
        .map(({ id: value, first_name, last_name }) => ({ key: `${first_name} ${last_name}`.trim(), value }))
    },
    categoryOptionLanguages() {
      if (!this.formData.site_group_id || !this.languageSites) return []

      return this.languageSites.data
        .filter(({ site: { site_group_id } }) => site_group_id === this.formData.site_group_id)
        .map(({ language: { name: key, id: value } }) => ({ key, value }))
    },
    isCreateDisabled() {
      return this.requiredFields.some(field => !isSet(this.formData[field]))
    },
    modalTitle() {
      return this.inheritFields.site_group_id ? `Add Translated News Article` : 'Create News Article'
    },
    requiredFields() {
      return this.schema?.required ? [...this.schema.required, 'site_group_id'] : []
    },
    selectedLanguage() {
      if (!this.languageSites?.data?.length) return null
      return this.languageSites.data
        .filter(({ language: { id, code } }) => id === this.formData.language_id || code === 'en')
        .find(({ site: { site_group_id } }) => site_group_id === this.formData.site_group_id)
    },
  },
  watch: {
    selectedLanguage: {
      handler() {
        this.handleLanguageChange()
      },
      immediate: true,
    },
    'formData.site_id': {
      async handler(siteId) {
        if (!siteId) return
        await this.fetchOptions(siteId)
      },
      immediate: true,
    },
    inheritFields: {
      async handler() {
        this.setInheritedFields()
      },
      immediate: true,
      deep: true,
    },
  },
  async mounted() {
    this.loading = true
    this.setInheritedFields()
    const paginate = { perPage: null }
    await this.fetchAuthors({ paginate })
    await this.fetchSiteGroups({ paginate })
    await this.fetchLanguageSites({ paginate })
    this.loading = false
  },
  methods: {
    ...mapActions({
      fetchSiteGroups: 'siteGroups/fetch',
      fetchLanguageSites: 'languageSites/fetch',
    }),
    async fetchAuthors(paginate) {
      const { data } = await authors.getAll({ paginate })
      this.authors = data
    },
    async fetchOptions(siteId) {
      this.loading = true
      this.options = await wordpress.getOptions(siteId)
      this.loading = false
    },
    handleLanguageChange() {
      if (this.selectedLanguage) {
        if (!this.inheritFields.site_id) this.$set(this.formData, 'site_id', this.selectedLanguage.site_id)
        if (!this.inheritFields.language_id) this.$set(this.formData, 'language_id', this.selectedLanguage.language_id)
      }
    },
    async onCreateConfirm() {
      if (!this.$refs.form.validate()) return
      try {
        const { id } = await news.create(this.formData)
        this.$notify({
          type: 'success',
          text: 'Successfully created new article',
        })
        this.$router.push({ name: 'news-articles.manage', params: { id } })
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to create new article',
        })
      }
    },
    onCreateCancel() {
      this.reset()
      this.$emit('input', false)
    },
    onInput({ context, model, value }) {
      if (context) {
        if (this.formData[model]) {
          this.$set(this.formData[model], context, value)
        } else {
          this.$set(this.formData, model, { [context]: value })
        }
      } else this.$set(this.formData, model, value)
    },
    reset() {
      this.formData = { auto_translate: false }
      this.setInheritedFields()
    },
    setInheritedFields() {
      Object.entries(this.inheritFields).forEach(([key, value]) => {
        this.$set(this.formData, key, value)
      })
    },
  },
}
</script>
